import React, { useEffect, useRef, useState } from 'react';
import {
    createChanceRound,
    GameResultOverlay,
    GameShell,
} from '../shared';
import { formatGameCopy } from './gameCopy';

const LEVEL_UP_STREAK = 2;

function ChanceCardsGame({
    copy = {},
    initialLevel = 1,
    onRunComplete = null,
    roundLimit = 14,
    showResults = true,
    ...props
}) {
    const startLevel = Math.max(1, Math.min(10, Math.round(Number(initialLevel) || 1)));
    const completionReportedRef = useRef(false);
    const [round, setRound] = useState(1);
    const [difficulty, setDifficulty] = useState(startLevel);
    const [trial, setTrial] = useState(() => createChanceRound(startLevel));
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const maxRounds = Math.max(1, Math.round(Number(roundLimit) || 14));
    const done = round > maxRounds;

    const reset = () => {
        completionReportedRef.current = false;
        setRound(1);
        setDifficulty(startLevel);
        setTrial(createChanceRound(startLevel));
        setChoice('');
        setScore(0);
        setStreak(0);
    };

    const choose = (side) => {
        if (done || choice) {
            return;
        }

        const correct = side === trial.correctSide;
        const nextStreak = correct ? streak + 1 : 0;
        setScore((value) => value + (correct ? 1 : 0));
        setStreak(nextStreak);
        setDifficulty((current) => (
            correct && nextStreak % LEVEL_UP_STREAK === 0
                ? Math.min(10, current + 1)
                : current
        ));
        setChoice(side);
    };

    const nextRound = () => {
        const nextRound = round + 1;
        setRound(nextRound);
        setChoice('');
        if (nextRound <= maxRounds) {
            setTrial(createChanceRound(difficulty));
        }
    };

    useEffect(() => {
        if (!choice || done) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            nextRound();
        }, 780);

        return () => window.clearTimeout(timer);
    }, [choice, done]);

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.({
            level: difficulty,
            maxScore: maxRounds,
            score,
            streak,
        });
    }, [difficulty, done, maxRounds, onRunComplete, score, streak]);

    const correctChoice = choice === trial.correctSide;
    const streakLabel = formatGameCopy(copy.streak, { count: streak });
    const options = [
        { side: 'left', ...trial.left },
        { side: 'right', ...trial.right },
    ];

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: difficulty, maxScore: maxRounds, score, streak }}
            tone={correctChoice || done ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__odds-prompt">{copy.chancePrompt}</div>
            <div className="UFOV-brain-games__odds-board">
                {options.map((option, index) => {
                    const picked = choice === option.side;
                    const correct = choice && trial.correctSide === option.side;

                    return (
                        <button
                            aria-label={formatGameCopy(copy.chanceOdds, {
                                number: index + 1,
                                total: option.total,
                                wins: option.wins,
                            })}
                            className={`UFOV-brain-games__odds-card ${picked ? 'is-picked' : ''} ${correct ? 'is-correct' : ''}`.trim()}
                            data-ratio={option.ratio.toFixed(4)}
                            data-side={option.side}
                            data-total={option.total}
                            data-wins={option.wins}
                            disabled={done || Boolean(choice)}
                            key={`${trial.id}-${option.side}`}
                            onClick={() => choose(option.side)}
                            type="button"
                        >
                            <span className="UFOV-brain-games__odds-fraction" aria-hidden="true">
                                <span className="UFOV-brain-games__odds-number">{option.wins}</span>
                                <span className="UFOV-brain-games__odds-line" />
                                <span className="UFOV-brain-games__odds-number">{option.total}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="UFOV-brain-games__odds-streak" aria-label={streakLabel}>{streakLabel}</div>
            {done && showResults ? (
                <GameResultOverlay
                    metrics={{ level: difficulty, maxScore: maxRounds, score, streak }}
                    onRestart={reset}
                />
            ) : null}
        </GameShell>
    );
}

export default ChanceCardsGame;
